// src/pages/Messages/index.js
//
// Mesajlar — hasta ↔ klinik yazışması (spec 2026-10-05 §12, modül `messaging`).
// Masaüstünde iki bölme (solda konuşmalar, sağda akış), dar ekranda tek bölme.
// Gerçek zamanlı kanal yok: sayfa görünürken 20 sn'de bir yenilenir.
// URL: ?konusma=<conversationId> ya da ?hasta=<patientId> (hasta sayfasındaki
// "Mesaj gönder" kısayolu; konu yoksa ilk mesajla oluşur).
// İzinler: görüntüleme messages:read (rota/menü), yazma messages:send.
// WhatsApp (spec 2026-10-06): kanal seçici + teslim tiki; whatsapp:contacts izniyle
// üstte "Hastalar / Yeni kişiler" seçimi (?gorunum=yeni-kisiler&kisi=<contactId>).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import useDebouncedSearch from "../../hooks/useDebouncedSearch";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Link, useMediaQuery, useTheme } from "@mui/material";
import { ForumOutlined as ForumIcon, ChatBubbleOutlineRounded as ChatIcon } from "@mui/icons-material";
import { PageHeaderBar } from "../../components/molecules";
import { EmptyState, SegmentedControl } from "../../components/atoms";
import useAuth from "../../hooks/useAuth";
import usePageVisible from "../../hooks/usePageVisible";
import { useHasPermission } from "../../hooks/useHasPermission";
import { useAlert } from "../../context/AlertProvider";
import { useClinicTheme } from "../../theme";
import { COLORS } from "../../styles/uiConstants";
import {
  messageApi,
  useListConversationsQuery,
  useMarkMessagesReadMutation,
  useMessageThreadQuery,
  useOlderMessagesMutation,
  useSendMessageMutation,
  useSendMessageTemplateMutation,
} from "../../store/messageApi";
import ConversationList from "./ConversationList";
import MessageThread from "./MessageThread";
import ContactsView from "./ContactsView";
import useTerms from "../../hooks/useTerms";

const POLL_MS = 20 * 1000;
const LIST_LIMIT = 100;
const THREAD_LIMIT = 40;

export default function Messages() {
  const terms = useTerms();
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { palette } = useClinicTheme();
  const { showError, showSuccess, showWarning } = useAlert();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down("md"));
  const visible = usePageVisible();
  const canSend = useHasPermission("messages:send");
  const canOpenPatient = useHasPermission("patients:read");
  const canContacts = useHasPermission("whatsapp:contacts");

  const [searchParams, setSearchParams] = useSearchParams();
  const contactsView = canContacts && searchParams.get("gorunum") === "yeni-kisiler";
  const contactId = contactsView ? searchParams.get("kisi") : null;
  const conversationId = contactsView ? null : searchParams.get("konusma");
  const patientId = contactsView || conversationId ? null : searchParams.get("hasta");
  const hasSelection = Boolean(conversationId || patientId);

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const { debounced, pending: searchPending } = useDebouncedSearch(search);
  const pollingInterval = visible ? POLL_MS : 0;

  // ── Konuşma listesi ──
  const listArgs = { token, search: debounced.trim() || undefined, filter, limit: LIST_LIMIT };
  // data: sayaçlar (rozetler) arama sırasında sıfıra düşmesin diye son sonuç.
  // currentData: liste — arama/filtre değişince yeni sonuç gelene kadar boş, iskelet çizilir.
  // isFetching tek başına kullanılmaz; yoksa her yoklamada (polling) iskelet yanıp söner.
  const {
    data: listRes,
    currentData: currentListRes,
    isFetching: listFetching,
  } = useListConversationsQuery(listArgs, {
    skip: !token,
    pollingInterval,
  });
  const conversations = useMemo(
    () => currentListRes?.data?.conversations || [],
    [currentListRes]
  );
  const listLoading = searchPending || (listFetching && !currentListRes);

  // ── Seçili konu ──
  const threadArgs = conversationId
    ? { token, conversationId, limit: THREAD_LIMIT }
    : { token, patientId, limit: THREAD_LIMIT };
  // currentData: başka konuya geçerken önceki konunun mesajları bir an görünmesin
  const {
    currentData: threadRes,
    isLoading: threadLoading,
    isFetching: threadFetching,
    isError: threadError,
  } = useMessageThreadQuery(threadArgs, { skip: !token || !hasSelection, pollingInterval });
  const thread = hasSelection ? threadRes?.data || null : null;
  const threadConvId = thread?.conversation?._id || null;

  // Hasta id'siyle açılmış ama konusu zaten varsa URL konu id'sine geçer
  useEffect(() => {
    if (patientId && threadConvId) setSearchParams({ konusma: threadConvId }, { replace: true });
  }, [patientId, threadConvId, setSearchParams]);

  // ── Eski sayfalar ──
  const [older, setOlder] = useState([]);
  const [olderHasMore, setOlderHasMore] = useState(null);
  const [fetchOlder, { isLoading: loadingOlder }] = useOlderMessagesMutation();
  const selectionKey = conversationId || patientId || "";
  useEffect(() => {
    setOlder([]);
    setOlderHasMore(null);
  }, [selectionKey]);

  const hasMoreOlder = olderHasMore === null ? Boolean(thread?.hasMore) : olderHasMore;
  const loadOlder = async () => {
    const first = older[0] || thread?.messages?.[0];
    if (!first || !threadConvId) return;
    try {
      const res = await fetchOlder({ token, conversationId: threadConvId, before: first.createdAt, limit: THREAD_LIMIT }).unwrap();
      setOlder((prev) => [...(res?.data?.messages || []), ...prev]);
      setOlderHasMore(Boolean(res?.data?.hasMore));
    } catch (err) {
      showError(err?.data?.message || "Eski mesajlar yüklenemedi");
    }
  };

  // ── Okundu ──
  const [markRead] = useMarkMessagesReadMutation();
  const unread = thread?.conversation?.unread || 0;
  const lastAt = thread?.conversation?.lastMessageAt;
  useEffect(() => {
    if (!threadConvId || unread <= 0 || !visible) return;
    markRead({ token, conversationId: threadConvId }).catch(() => {});
  }, [threadConvId, unread, lastAt, visible, token, markRead]);

  // ── Gönder ──
  const [sendMessage, { isLoading: sending }] = useSendMessageMutation();
  const handleSend = async (text, channel = "app") => {
    try {
      const body = threadConvId ? { conversationId: threadConvId } : { patientId };
      const res = await sendMessage({ token, ...body, text, channel }).unwrap();
      const newId = res?.data?.conversation?._id;
      if (!threadConvId && newId) setSearchParams({ konusma: newId }, { replace: true });
      return true;
    } catch (err) {
      showError(err?.data?.message || "Mesaj gönderilemedi");
      return false;
    }
  };

  // ── WhatsApp manuel şablonu (24 saat penceresi kapalıyken listeden seçilir) ──
  const [sendTemplate, { isLoading: sendingTemplate }] = useSendMessageTemplateMutation();
  const handleSendTemplate = async (templateKey) => {
    try {
      const body = threadConvId ? { conversationId: threadConvId } : { patientId };
      await sendTemplate({ token, ...body, templateKey }).unwrap();
      showSuccess("WhatsApp şablonu gönderildi");
    } catch (err) {
      showError(err?.data?.message || "Şablon gönderilemedi");
    }
  };

  // ── Hastalar / Yeni kişiler ──
  const contactsUnread = listRes?.data?.counts?.whatsappContacts || 0;
  const switchView = (v) =>
    setSearchParams(v === "contacts" ? { gorunum: "yeni-kisiler" } : {});
  const switcher = canContacts ? (
    <Box sx={{ px: { xs: 2, md: 2.5 }, pt: 2, flexShrink: 0 }}>
      <SegmentedControl
        ariaLabel="Mesaj listesi"
        fullWidth
        value={contactsView ? "contacts" : "patients"}
        onChange={switchView}
        options={[
          { value: "patients", label: terms.patients },
          { value: "contacts", label: contactsUnread ? `Yeni kişiler · ${contactsUnread}` : "Yeni kişiler" },
        ]}
        palette={palette}
      />
    </Box>
  ) : null;
  const handleLinked = (res) => {
    dispatch(messageApi.util.invalidateTags(["Conversations", "Thread"]));
    if (res?.phoneMismatch) {
      showWarning(
        `${terms.patientGen} kayıtlı telefonu bu numaradan farklı; bu numaradan gelen mesajlar yine bu ${terms.patientDatLower} düşecek.`
      );
    } else {
      showSuccess(`Kişi ${terms.patientDatLower} bağlandı`);
    }
    if (res?.conversationId) setSearchParams({ konusma: res.conversationId });
    else if (res?.patientId) setSearchParams({ hasta: res.patientId });
    else setSearchParams({});
  };

  const select = useCallback(
    (c) => (c.draft ? null : setSearchParams({ konusma: c._id })),
    [setSearchParams]
  );
  const clearSelection = () => setSearchParams({});
  const openPatient =
    canOpenPatient && thread?.patient?._id ? () => navigate(`/Hastalar/${thread.patient._id}`) : undefined;

  // Hasta sayfasından "Mesaj gönder" ile gelindi ve henüz konu yok: listenin başında
  // seçili bir TASLAK satırı ("gönderilmedi"). Yalnız ekranda — konu sunucuda ilk
  // mesajla oluşur; gönderilmeden çıkılırsa iz kalmaz.
  const draft =
    patientId && thread && !thread.conversation && thread.patient
      ? { _id: `draft:${patientId}`, patient: thread.patient, draft: true }
      : null;
  const listItems = draft && !search.trim() && filter === "all" ? [draft, ...conversations] : conversations;
  const selectedId = draft ? draft._id : threadConvId || conversationId;
  const showList = !narrow || !hasSelection;
  const showThread = !narrow || hasSelection;

  const emptyAction = canOpenPatient ? (
    <Link component="button" type="button" onClick={() => navigate("/Hastalar")} sx={{ color: palette.primary, verticalAlign: "baseline", fontSize: "inherit" }}>
      {terms.patientLower} listesine gidin
    </Link>
  ) : null;

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: COLORS.white, minHeight: 0 }}>
      <PageHeaderBar
        icon={<ForumIcon />}
        title="Mesajlar"
        subtitle={canContacts ? `${terms.patients}ınızla uygulama ve WhatsApp yazışmaları` : `${terms.patients}ınızla uygulama içi yazışma`}
      />
      {contactsView ? (
        <ContactsView
          token={token}
          contactId={contactId}
          onSelectContact={(id) => setSearchParams(id ? { gorunum: "yeni-kisiler", kisi: id } : { gorunum: "yeni-kisiler" })}
          onLinked={handleLinked}
          pollingInterval={pollingInterval}
          visible={visible}
          narrow={narrow}
          switcher={switcher}
          palette={palette}
        />
      ) : (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "340px 1fr", lg: "380px 1fr" },
        }}
      >
        {showList ? (
          <Box sx={{ minHeight: 0, display: "flex", flexDirection: "column", borderRight: { md: `1px solid ${COLORS.slate200}` } }}>
            {switcher}
            <Box sx={{ flex: 1, minHeight: 0 }}>
            <ConversationList
              conversations={listItems}
              loading={listLoading}
              selectedId={selectedId}
              onSelect={select}
              search={search}
              onSearchChange={setSearch}
              filter={filter}
              onFilterChange={setFilter}
              counts={listRes?.data?.counts}
              total={listRes?.data?.total || 0}
              palette={palette}
              emptyAction={emptyAction}
            />
            </Box>
          </Box>
        ) : null}
        {showThread ? (
          <Box sx={{ minHeight: 0, display: "flex", flexDirection: "column" }}>
            {hasSelection ? (
              <MessageThread
                thread={thread}
                loading={threadLoading || (threadFetching && !thread)}
                error={threadError}
                olderMessages={older}
                hasMoreOlder={hasMoreOlder}
                loadingOlder={loadingOlder}
                onLoadOlder={loadOlder}
                onSend={handleSend}
                sending={sending}
                onSendTemplate={handleSendTemplate}
                sendingTemplate={sendingTemplate}
                canSendPermission={canSend}
                onBack={narrow ? clearSelection : undefined}
                onOpenPatient={openPatient}
                palette={palette}
              />
            ) : (
              <EmptyState
                variant="illustrated"
                icon={<ChatIcon />}
                title="Bir konuşma seçin"
                description={`Soldaki listeden bir ${terms.patientLower} seçerek yazışmayı görüntüleyin.`}
                sx={{ flex: 1 }}
              />
            )}
          </Box>
        ) : null}
      </Box>
      )}
    </Box>
  );
}
